<template>
  <div class="wrongbook-mobile-container">
    <!-- 右下角操作按钮：问号 + 查询 + 统计 -->
    <div class="mobile-action-buttons">
      <el-button class="mobile-icon-btn" @click="showQuestionDialog">
        <i class="el-icon-question"></i>
      </el-button>
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
        <i class="el-icon-data-analysis"></i>
      </el-button>
    </div>

    <!-- 顶部标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">错题本</h1>
    </div>

    <!-- 错误类型卡片列表 -->
    <div class="error-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="errorList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-warning-outline"></i>
          <span class="no-data-title">暂无数据</span>
          <p class="no-data-tips">没有查询到错题统计，请调整时间范围</p>
        </div>
      </div>

      <template v-else>
        <div 
          v-for="err in displayErrorList"
          :key="err.error_code"
          class="error-card"
          @click="handleCardClick(err)"
        >
          <!-- 右上角难度三角（展示平均难度百分比） -->
          <div class="difficulty-indicator">
            <div class="triangle-background"
                 :style="{ 'border-color': `transparent ${getDifficultyColorByPercentage(err.avg_difficulty)} transparent transparent` }">
            </div>
            <span class="difficulty-value">{{ formatPercentage(err.avg_difficulty) }}</span>
          </div>

          <!-- 卡片主体 -->
          <div class="error-card-main">
            <div class="error-header">
              <h3 class="error-name">{{ err.error_name }}</h3>
            </div>

            <div class="error-info">
              <div class="error-data-grid">
                <!-- 平均分/订正分 -->
                <div class="error-score">
                  <span class="score-label">平均分数：</span>
                  <div class="score-value-container">
                    <span class="score-value" :style="{ color: getScoreColor(err.question_score_avg), fontWeight: 'bold' }">{{ toFixed2(err.question_score_avg) }}</span>
                    <span class="score-separator">/</span>
                    <span class="score-value" :style="{ color: getScoreColor(err.question_score_fix_avg), fontWeight: 'bold' }">{{ toFixed2(err.question_score_fix_avg) }}</span>
                  </div>
                </div>

                <!-- 数量：未订正/总题目 -->
                <div class="error-question-count">
                  <span class="count-label">题目数：</span>
                  <div class="count-value-container">
                    <span class="count-value pending-count">{{ err.pending_questions }}</span>
                    <span class="count-separator">/</span>
                    <span class="count-value">{{ err.question_nums }}</span>
                  </div>
                </div>
              </div>

              <!-- 订正率进度条 -->
              <div class="fix-rate-container">
                <div class="fix-rate-header">
                  <span class="fix-rate-label">订正率：</span>
                  <span class="fix-rate-value" :style="{ color: getScoreColor(err.fix_rate), fontWeight: '600' }">{{ toFixed2(err.fix_rate) }}%</span>
                </div>
                <el-progress :percentage="err.fix_rate" :show-text="false" :stroke-width="5" :color="getScoreColor(err.fix_rate)" />
              </div>
            </div>
          </div>
        </div>

        <!-- 加载更多 / 加载中 / 已到底 -->
        <div v-if="hasMore && !loading && displayErrorList.length > 0" class="load-more">
          <el-button type="text" @click="loadMore">加载更多</el-button>
        </div>
        <div v-if="loading && displayErrorList.length > 0" class="loading-more">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-if="!hasMore && displayErrorList.length > 0" class="no-more">
          <span>------已经到底啦------</span>
          <div class="copyright-info">
            <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
          </div>
        </div>
      </template>
    </div>

    <!-- 统计弹窗（原始实现） -->
    <div v-show="statisticsDialogVisible">
      <div @click="statisticsDialogVisible = false" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 5000;"></div>
      <div style="position: fixed; top: 10vh; left: 4%; width: 92%; max-width: 520px; max-height: 80vh; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.3); overflow: hidden; z-index: 5001;">
        <div @click="statisticsDialogVisible = false" style="position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.15); color: #909399; display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; z-index: 5002;">×</div>
        <div style="padding: 12px 10px; max-height: 80vh; overflow-y: auto;">
          <h3 style="font-size: 18px; font-weight: 600; color: #303133; margin: 0 0 12px 0; padding-bottom: 10px; border-bottom: 1px solid #ebeef5;">错题统计</h3>
          <div class="mobile-statistics-grid">
            <div class="stat-item">
              <div class="stat-value">{{ formatNumber(totalWrongCount) }}</div>
              <div class="stat-label"><i class="el-icon-notebook-1"></i>错题数</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ toFixed2(totalFixRate) }}%</div>
              <div class="stat-label"><i class="el-icon-data-line"></i>总体订正率</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 问号说明弹窗 -->
    <div v-show="questionDialogVisible">
      <div @click="questionDialogVisible = false" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 5000;"></div>
      <div style="position: fixed; top: 10vh; left: 4%; width: 92%; max-width: 520px; max-height: 80vh; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.3); overflow: hidden; z-index: 5001;">
        <div @click="questionDialogVisible = false" style="position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.15); color: #909399; display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; z-index: 5002;">×</div>
        <div style="padding: 12px 10px 20px 10px; max-height: 80vh; overflow-y: auto;">
          <h3 style="font-size: 18px; font-weight: 600; color: #303133; margin: 0 0 12px 0; padding-bottom: 10px; border-bottom: 1px solid #ebeef5;">错误类型说明</h3>
          <div class="error-type-description" style="padding-bottom: 20px;">
            <p><strong>1. 算法未掌握</strong>：未掌握核心知识点、算法原理或其适用性；</p>
            <ul>
              <li>【学透】深入理解定义、原理、推导、复杂度</li>
              <li>【对比】区分相似概念，明确应用场景</li>
              <li>【复习】定期回顾基础知识</li>
            </ul>
            <p><strong>2. 思路/逻辑偏差</strong>：思路框架或解题逻辑有缺陷，无法构建正确解题路径；</p>
            <ul>
              <li>【拆解】将问题分解，逐个击破</li>
              <li>【模拟】用小数据手动演算，验证思路</li>
              <li>【学习】学习他人优秀解法，寻找更优思路</li>
            </ul>
            <p><strong>3. 实现/细节错误</strong>：思路正确，但代码细节处理不当，导致程序出错；</p>
            <ul>
              <li>【细抠】关注边界、初始化、数据类型、语句逻辑</li>
              <li>【调试】熟练使用调试器，定位问题</li>
              <li>【规范】养成良好的编码习惯</li>
            </ul>
            <p><strong>4. 效率/复杂度问题</strong>：算法或实现效率低，导致超时 (TLE) 或超内存 (MLE)；</p>
            <ul>
              <li>【估算】优先评估算法时间/空间复杂度</li>
              <li>【优化】学习常用优化技巧（如剪枝、数据结构）</li>
              <li>【卡点】针对性练习时间/空间敏感题</li>
            </ul>
            <p><strong>5. 边界/特殊情况遗漏</strong>：未考虑题目中的特殊数据、极端情况或边界条件；</p>
            <ul>
              <li>【预判】思考各种可能的小数据、负数、空集、全同等情况</li>
              <li>【测试】编写专门测试边界的用例</li>
              <li>【总结】建立边界条件检查清单</li>
            </ul>
            <p><strong>6. 题意理解偏差</strong>：对题目描述、要求、输入输出等理解有误；</p>
            <ul>
              <li>【精读】慢读、细读，划出关键词</li>
              <li>【明确】确认输入输出格式、数据范围、约束条件</li>
              <li>【举例】用自己的话复述题目</li>
            </ul>
            <p><strong>7. 工具/环境问题</strong>：编程语言特性、编译器、IDE、提交系统等非算法本身的细节问题；</p>
            <ul>
              <li>【熟悉】了解常用语言特性、库函数用法</li>
              <li>【检查】留意编译警告，熟悉评测环境</li>
              <li>【记录】记录常见坑点</li>
            </ul>
            <p><strong>8. 心理性错误</strong>：受情绪、心态、比赛压力等因素影响导致的技术失误；</p>
            <ul>
              <li>【训练】模拟赛中培养抗压能力</li>
              <li>【调整】保持积极心态，学会自我鼓励</li>
              <li>【暂停】遇到困难时，短暂休息，深呼吸</li>
            </ul>
            <p><strong>9. 其他/未分类错误</strong>：暂时难以明确归类，或属特定情况的错误；</p>
            <ul>
              <li>【分析】尝试将其归入现有分类</li>
              <li>【记录】暂存，等待进一步研究或求助</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选弹窗（原始实现） -->
    <div v-show="filterDialogVisible">
      <div @click="filterDialogVisible = false" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 5000;"></div>
      <div style="position: fixed; top: 10vh; left: 50%; transform: translateX(-50%); width: 95%; max-width: 520px; max-height: 80vh; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.3); overflow: hidden; z-index: 5001;">
        <div @click="filterDialogVisible = false" style="position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.15); color: #909399; display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; z-index: 5002;">×</div>
        <div style="padding: 15px 15px 10px; max-height: 80vh; overflow-y: auto;">
          <h3 style="font-size: 18px; font-weight: 600; color: #303133; margin: 0 0 12px 0; padding-bottom: 10px; border-bottom: 1px solid #ebeef5;">查询条件</h3>
          <el-form :model="searchForm" class="filter-form" size="small" label-width="70px">
            <el-form-item label="时间范围">
              <div class="date-range-container">
                <el-date-picker v-model="searchForm.start_time" type="date" placeholder="开始日期" value-format="yyyy-MM-dd" size="small" style="width: 100%;" class="date-picker-item" />
                <span class="date-separator">至</span>
                <el-date-picker v-model="searchForm.end_time" type="date" placeholder="结束日期" value-format="yyyy-MM-dd" size="small" style="width: 100%;" class="date-picker-item" />
              </div>
            </el-form-item>
          </el-form>
          <div class="dialog-footer filter-footer">
            <el-button size="small" @click="handleReset">重置</el-button>
            <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误类型详情弹窗（原始实现） -->
    <div v-show="detailDialogVisible">
      <div @click="closeDetailDialog" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 5000;"></div>
      <div style="position: fixed; top: 10vh; left: 50%; transform: translateX(-50%); width: 95%; max-width: 520px; max-height: 80vh; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.3); overflow: hidden; z-index: 5001;">
        <div @click="closeDetailDialog" style="position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.15); color: #909399; display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; z-index: 5002;">×</div>
        <div style="overflow-y: hidden; display: flex; flex-direction: column; padding: 15px 15px 0; height: 80vh;">
          <div v-if="currentError" class="tag-detail" style="background-color: #ffffff;">
            <div class="detail-fixed-header">
              <h3 class="tag-title">{{ currentError.error_name }}</h3>

              <!-- 指标卡两行三列布局（参考 TagMobile） -->
              <div class="tag-stats-card">
                <div class="tag-stats-row">
                  <div class="tag-stat-item">
                    <span class="stat-label">平均分数</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentError.question_score_avg), fontWeight: 'bold' }">{{ toFixed2(currentError.question_score_avg) }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">平均订正分</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentError.question_score_fix_avg), fontWeight: 'bold' }">{{ toFixed2(currentError.question_score_fix_avg) }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">平均难度</span>
                    <span class="stat-value difficulty-badge" :style="{ backgroundColor: getDifficultyColorByPercentage(currentError.avg_difficulty) }">{{ formatPercentage(currentError.avg_difficulty) }}</span>
                  </div>
                </div>
                <div class="tag-stats-row">
                  <div class="tag-stat-item">
                    <span class="stat-label">题目数</span>
                    <span class="stat-value">{{ currentError.question_nums }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">未订正题目数</span>
                    <span class="stat-value">{{ currentError.pending_questions }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">订正率</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentError.fix_rate), fontWeight: 'bold' }">{{ toFixed2(currentError.fix_rate) }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 题目列表（仅错题） -->
            <div class="detail-scrollable-content" @scroll="handleQuestionScroll">
              <div v-if="displayQuestionList.length > 0" class="question-list">
                <div 
                  v-for="question in displayQuestionList" 
                  :key="question.unique_id || question.question_code" 
                  class="question-item"
                  :class="{ 'clickable-replay': question.has_replay }"
                  @click="handleQuestionItemClick(question, $event)">
                  <div v-if="question.question_score === 100 || (question.question_score < 100 && question.question_score_fix === 100)" class="question-fix-stamp">
                    <div class="stamp-content" :class="{ 'ak-stamp': question.question_score === 100 }">{{ question.question_score === 100 ? 'AC' : '订' }}</div>
                  </div>

                  <div class="question-header">
                    <span class="question-seq" :style="{ backgroundColor: getDifficultyColor(question.question_level || '未定义'), color: 'white' }">{{ question.question_seq }}</span>
                    <span class="question-name">
                      <a v-if="question.question_url" :href="question.question_url" target="_blank" rel="noopener" class="question-name-link">{{ question.question_name }}</a>
                      <span v-else>{{ question.question_name }}</span>
                      <i v-if="question.has_replay" class="el-icon-video-camera replay-icon-badge" :title="'查看复盘'" style="margin-left: 2px;"></i>
                    </span>
                  </div>

                  <div class="question-info">
                    <div class="game-name-row">
                      <i class="el-icon-trophy"></i>
                      <span>{{ question.game_name }}</span>
                    </div>
                    <div class="score-row">
                      <div class="score-cell">
                        <span class="score-label">得分：</span>
                        <i v-if="question.question_score === 100" class="el-icon-medal-1" :style="{ color: getScoreColor(question.question_score), marginRight: '4px' }" />
                        <span class="score-value" :style="{ color: getScoreColor(question.question_score), fontWeight: 'bold' }">{{ question.question_score }}</span>
                      </div>
                      <div class="score-cell">
                        <span class="score-label">订正：</span>
                        <i v-if="question.question_score_fix === 100" class="el-icon-medal-1" :style="{ color: getScoreColor(question.question_score_fix), marginRight: '4px' }" />
                        <span class="score-value" :style="{ color: getScoreColor(question.question_score_fix), fontWeight: 'bold' }">{{ question.question_score_fix }}</span>
                      </div>
                    </div>
                    <!-- 标签：得分下面一行 -->
                    <div v-if="question.tags && question.tags.length" class="question-tags-row">
                      <el-tag
                        v-for="(tagName, idx) in question.tags"
                        :key="(tagName || idx) + '-m-tag'"
                        :style="{ backgroundColor: getTagColorByName(tagName), borderColor: getTagColorByName(tagName), color: 'white', marginRight: '4px', marginTop: '6px' }"
                        size="mini">
                        {{ tagName }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="no-questions">
                <i class="el-icon-document"></i>
                <span>暂无题目信息</span>
              </div>

              <div v-if="questionLoading && displayQuestionList.length > 0" class="loading-more">
                <i class="el-icon-loading"></i>
                <span>加载中...</span>
              </div>
              <div v-if="!hasMoreQuestions && displayQuestionList.length > 0" class="question-no-more">
                <span>------已经到底啦------</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  
  <!-- 复盘弹窗（移动端） -->
  <div v-show="replayDialogVisible">
    <div @click="replayDialogVisible = false" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 6000;"></div>
    <div style="position: fixed; top: 10vh; left: 4%; width: 92%; max-width: 520px; max-height: 80vh; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.3); overflow: hidden; z-index: 6001;">
      <div @click="replayDialogVisible = false" style="position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.15); color: #909399; display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; z-index: 6002;">×</div>
      <div style="padding: 12px 10px; max-height: 80vh; overflow-y: auto;">
        <h3 style="font-size: 18px; font-weight: 600; color: #303133; margin: 0 0 12px 0; padding-bottom: 10px; border-bottom: 1px solid #ebeef5;">{{ replayQuestionTitle ? `${replayQuestionTitle} - 复盘内容` : '复盘内容' }}</h3>
        <div v-loading="replayLoading">
          <markdown-viewer v-if="replayContent" :content="replayContent" />
          <div v-else style="color:#909399;">暂无复盘内容</div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

 

<script>
import { getWrongbookStatistics as fetchStats, getWrongbookQuestions as fetchQuestions } from '@/api/wrongbook'
import { getAllTags } from '@/api/tag'
import { getQuestionReplay } from '@/api/question'
import MarkdownViewer from '@/components/MarkdownViewer.vue'

export default {
  name: 'WrongbookMobile',
  components: { MarkdownViewer },
  data() {
    return {
      loading: false,
      questionLoading: false,
      // 列表数据（错误类型）
      errorList: [],
      // 详情题目数据
      questionList: [],

      // 搜索表单
      searchForm: { start_time: '', end_time: '' },

      // 弹窗
      filterDialogVisible: false,
      statisticsDialogVisible: false,
      detailDialogVisible: false,
      questionDialogVisible: false,

      // 当前选择的错误类型
      currentError: null,

      // 前端分页（错误类型）
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,

      // 前端分页（题目）
      questionPage: 1,
      questionPageSize: 20,
      questionTotal: 0,
      hasMoreQuestions: true,

      // 加载节流标志
      _loadingMore: false,
      _loadingMoreQuestions: false,

      // 滚动头部隐藏/显示
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10,

      // 统计概览
      totalWrongCount: 0,
      totalFixRate: 0,
      // 标签字典（用于颜色映射）
      tagsDict: [],
      // 复盘弹窗
      replayDialogVisible: false,
      replayLoading: false,
      replayContent: '',
      replayQuestionTitle: ''
    }
  },
  created() {
    this.fetchErrorList()
    this.fetchAllTags()
  },
  computed: {
    displayErrorList() {
      const start = 0
      const end = this.currentPage * this.pageSize
      return this.errorList.slice(start, end)
    },
    displayQuestionList() {
      const start = 0
      const end = this.questionPage * this.questionPageSize
      return this.questionList.slice(start, end)
    }
  },
  methods: {
    async openReplay(question) {
      if (!question || !question.question_code) return
      this.replayDialogVisible = true
      this.replayLoading = true
      this.replayContent = ''
      this.replayQuestionTitle = question.question_name || ''
      try {
        const resp = await getQuestionReplay(question.question_code)
        if (resp && resp.code === 200) {
          this.replayContent = resp.data || ''
        } else {
          this.$message.error('获取复盘失败')
          this.replayDialogVisible = false
        }
      } catch (e) {
        this.$message.error('获取复盘失败')
        this.replayDialogVisible = false
      } finally {
        this.replayLoading = false
      }
    },
    handleQuestionItemClick(question, evt) {
      try {
        const path = evt.composedPath ? evt.composedPath() : (evt.path || [])
        const clickedLink = path.find && path.find(el => el && el.tagName === 'A')
        if (clickedLink) return
        if (question && question.has_replay) {
          this.openReplay(question)
        }
      } catch (_) {
        if (question && question.has_replay) {
          this.openReplay(question)
        }
      }
    },
    async fetchAllTags() {
      try {
        const { data } = await getAllTags()
        this.tagsDict = data || []
      } catch (e) { this.tagsDict = [] }
    },
    toFixed2(val) {
      if (val === null || val === undefined || isNaN(val)) return '0.00'
      return Number(val).toFixed(2)
    },
    formatPercentage(value) {
      if (value === null || value === undefined || isNaN(value)) return '0.00%'
      return this.toFixed2(value) + '%'
    },
    // 获取错题统计（前端分页）
    async fetchErrorList() {
      if (this.loading) return
      this.loading = true
      try {
        const res = await fetchStats({
          start_time: this.searchForm.start_time,
          end_time: this.searchForm.end_time
        })
        if (res && res.code === 200) {
          // 排序规则：未定义置底，名称升序（与PC一致）
          const list = (res.data || []).slice().sort((a, b) => {
            const ua = a.is_undefined ? 1 : 0
            const ub = b.is_undefined ? 1 : 0
            if (ua !== ub) return ua - ub
            return String(a.error_name || '').localeCompare(String(b.error_name || ''))
          })
          this.errorList = list
          this.total = list.length
          this.hasMore = this.currentPage * this.pageSize < this.total

          // 统计聚合（与PC一致）：总错题数、总体订正率
          const agg = this.errorList.reduce((acc, r) => {
            const wrongCount = Number(r.question_nums || 0)
            acc.total += wrongCount
            acc.fixed += Math.round((Number(r.fix_rate || 0) / 100) * wrongCount)
            return acc
          }, { total: 0, fixed: 0 })
          this.totalWrongCount = agg.total
          this.totalFixRate = agg.total === 0 ? 100 : (agg.fixed / agg.total) * 100
        } else {
          this.errorList = []
          this.total = 0
          this.hasMore = false
          this.totalWrongCount = 0
          this.totalFixRate = 0
        }
      } catch (e) {
        console.error('获取错题统计失败:', e)
        this.$message.error('获取错题统计失败')
        this.errorList = []
        this.total = 0
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    // 获取某错误类型下的题目列表（仅错题）
    async fetchQuestionList(errorCode) {
      if (this.questionLoading) return
      this.questionLoading = true
      // 重置题目分页
      this.questionPage = 1
      this.questionList = []
      try {
        const res = await fetchQuestions({
          start_time: this.searchForm.start_time,
          end_time: this.searchForm.end_time,
          error_code: errorCode
        })
        if (res && res.code === 200) {
          const all = (res.data || []).filter(item => Number(item.question_score) !== 100)
          this.questionList = all.map((item, index) => ({ ...item, unique_id: `${errorCode}_${index}_${Date.now()}` }))
          this.questionTotal = this.questionList.length
          this.hasMoreQuestions = this.questionPage * this.questionPageSize < this.questionTotal
        } else {
          this.questionList = []
          this.questionTotal = 0
          this.hasMoreQuestions = false
        }
      } catch (e) {
        console.error('获取错题明细失败:', e)
        this.$message.error('获取错题明细失败')
        this.questionList = []
        this.questionTotal = 0
        this.hasMoreQuestions = false
      } finally {
        this.questionLoading = false
      }
    },
    // 加载更多（错误类型）
    async loadMore() {
      if (!this.hasMore || this.loading) return
      this.currentPage++
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      this.hasMore = end < this.errorList.length
      this._loadingMore = false
    },
    // 加载更多（题目）
    async loadMoreQuestions() {
      if (!this.hasMoreQuestions || this.questionLoading) return
      this.questionPage++
      const start = (this.questionPage - 1) * this.questionPageSize
      const end = start + this.questionPageSize
      this.hasMoreQuestions = end < this.questionList.length
      this._loadingMoreQuestions = false
    },
    // 打开筛选
    showFilterDialog() {
      this.filterDialogVisible = true
    },
    // 打开统计
    showStatisticsDialog() {
      this.statisticsDialogVisible = true
    },
    // 打开问号说明
    showQuestionDialog() {
      this.questionDialogVisible = true
    },
    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.total = 0
      this.hasMore = true
      this._loadingMore = false
      this.fetchErrorList()
      this.filterDialogVisible = false
    },
    // 重置
    handleReset() {
      this.searchForm = { start_time: '', end_time: '' }
    },
    // 卡片点击
    handleCardClick(err) {
      this.currentError = err
      this.detailDialogVisible = true
      this.questionPage = 1
      this.questionList = []
      this.hasMoreQuestions = true
      this.questionTotal = 0
      this._loadingMoreQuestions = false
      if (this._scrollTimer) { clearTimeout(this._scrollTimer); this._scrollTimer = null }
      this.fetchQuestionList(err.error_code)
    },
    // 关闭详情
    closeDetailDialog() {
      this.detailDialogVisible = false
      if (this._scrollTimer) { clearTimeout(this._scrollTimer); this._scrollTimer = null }
      this.questionPage = 1
      this.questionList = []
      this.hasMoreQuestions = true
      this.questionTotal = 0
      this._loadingMore = false
      this._loadingMoreQuestions = false
    },
    // 列表滚动（错误类型）
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      const deltaY = scrollTop - this.lastScrollTop
      if (Math.abs(deltaY) > this.scrollThreshold) {
        this.headerVisible = deltaY <= 0
      }
      this.lastScrollTop = scrollTop
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        if (!this._loadingMore) {
          this._loadingMore = true
          this.loadMore()
        }
      }
    },
    // 详情滚动（题目）
    handleQuestionScroll(event) {
      if (this.questionLoading || !this.hasMoreQuestions) return
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      if (scrollHeight - scrollTop - clientHeight < 30) {
        if (!this._loadingMoreQuestions) {
          this._loadingMoreQuestions = true
          this.loadMoreQuestions()
        }
      }
    },
    // 颜色映射
    getDifficultyColor(level) {
      const colorMap = {
        '红题': '#F56C6C',
        '橙题': '#E6A23C',
        '黄题': '#F7BA2A',
        '绿题': '#67C23A',
        '蓝题': '#409EFF',
        '紫题': '#8E44AD',
        '黑题': '#2C3E50',
        '': '#909399'
      }
      return colorMap[level] || '#909399'
    },
    getDifficultyColorByPercentage(percentage) {
      if (percentage === null || percentage === undefined) return '#909399'
      if (percentage < 14.29) return '#F56C6C'
      if (percentage < 28.58) return '#E6A23C'
      if (percentage < 42.87) return '#F7BA2A'
      if (percentage < 57.15) return '#67C23A'
      if (percentage < 71.44) return '#409EFF'
      if (percentage < 85.72) return '#8E44AD'
      return '#2C3E50'
    },
    getScoreColor(score) {
      if (score === undefined || score === null) return '#606266'
      const percentage = score
      if (percentage >= 100) return '#237804'
      if (percentage >= 75) return '#52C41A'
      if (percentage >= 50) return '#F7BA2A'
      if (percentage >= 25) return '#E6A23C'
      return '#F56C6C'
    },
    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      return String(num)
    },
    // 标签色彩映射：复用 Question 页面逻辑
    getTagColorByCode(tagCode) {
      const colors = {
        'tag-color-1': '#409EFF',
        'tag-color-2': '#67C23A',
        'tag-color-3': '#E6A23C',
        'tag-color-4': '#F56C6C',
        'tag-color-5': '#8E44AD',
        'tag-color-6': '#16A085',
        'tag-color-7': '#D35400',
        'tag-color-8': '#2980B9',
        'tag-color-9': '#27AE60',
        'tag-color-10': '#C0392B'
      }
      const classes = ['tag-color-1','tag-color-2','tag-color-3','tag-color-4','tag-color-5','tag-color-6','tag-color-7','tag-color-8','tag-color-9','tag-color-10']
      const index = parseInt(String(tagCode).split('_')[1] || '0', 36) % classes.length
      return colors[classes[index]]
    },
    getTagColorByName(tagName) {
      const tag = this.tagsDict.find(t => t.tag_name === tagName)
      return tag ? this.getTagColorByCode(tag.tag_code) : '#409EFF'
    }
  }
}
</script>

<style lang="scss" scoped>
.wrongbook-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
}

/* 浮动操作按钮（与 TagMobile、QuestionMobile 保持一致） */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  z-index: 2001;
  width: 40px;
  align-items: center;
  padding: 0 !important;
  margin: 0 !important;
  .el-button { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
}

/* 问号弹窗样式 */
.error-type-description {
  line-height: 1.6;
  font-size: 13px;
  color: #303133;
}

.error-type-description p {
  margin: 8px 0;
  color: #303133;
}

.error-type-description p strong {
  color: #409EFF;
  font-weight: 600;
}

.error-type-description ul {
  margin: 4px 0 8px 0;
  padding-left: 16px;
}

.error-type-description li {
  margin: 2px 0;
  color: #606266;
  font-size: 12px;
}

.mobile-icon-btn {
  width: 40px !important;
  height: 40px !important;
  border-radius: 40px !important;
  background-color: rgba(255, 255, 255, 0.5) !important;
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  left: 0 !important;
  i { font-size: 20px !important; margin: 0 !important; padding: 0 !important; display: inline-flex !important; justify-content: center !important; align-items: center !important; width: 20px !important; height: 20px !important; }
  &:hover, &:focus { background-color: rgba(255, 255, 255, 0.8) !important; transform: scale(1.05) !important; }
  &:active { background-color: rgba(255, 255, 255, 0.9) !important; transform: scale(0.95) !important; }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  .page-title { font-size: 18px; font-weight: 600; color: #ffffff; margin: 0; }
  &.header-hidden { transform: translateY(-100%); opacity: 0; }
}

/* 列表容器 */
.error-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px;
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  margin: 0 auto;
  transition: padding-top 0.3s ease;
  &::-webkit-scrollbar { display: none; }
  &.header-hidden { padding-top: 10px; }
  .error-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    display: flex;
    align-items: flex-start;
    position: relative;
    border: 1px solid #ebeef5;
    &:active { transform: scale(0.98); box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06); }
    &:last-child { margin-bottom: 20px; }
    .difficulty-indicator {
      position: absolute;
      top: 0; right: 0; width: 60px; height: 60px; overflow: hidden; z-index: 1; pointer-events: none;
      .triangle-background { position: absolute; top: 0; right: 0; width: 0; height: 0; border-style: solid; border-width: 0 60px 60px 0; opacity: 0.85; }
      .difficulty-value { position: absolute; top: 12px; right: 12px; transform: rotate(45deg); color: white; font-size: 12px; font-weight: bold; text-align: center; white-space: nowrap; width: 30px; line-height: 1; text-shadow: 0 1px 1px rgba(0,0,0,0.3); letter-spacing: 0; }
    }
    .error-card-main {
      padding: 15px; padding-top: 10px; flex: 1; width: 100%;
      .error-header { display: flex; align-items: flex-start; margin-bottom: 0px; padding-right: 50px; min-height: 30px;
        .error-name { font-size: 16px; font-weight: 600; color: #303133; line-height: 1.4; word-break: break-word; margin: 0; padding: 0; flex: 1; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; }
      }
      .error-info { margin-top: 0px; border-top: 1px solid #f5f5f5; padding-top: 10px;
        .error-data-grid { display: grid; grid-template-columns: repeat(2, 1fr); grid-gap: 8px; margin-bottom: 12px; }
        .error-score, .error-question-count { font-size: 12px; display: flex; align-items: center; background-color: rgba(249, 250, 252, 0.8); border-radius: 6px; padding: 6px 10px; border: 1px solid #f0f2f5;
          .score-label, .count-label { color: #606266; font-weight: 500; white-space: nowrap; }
          .score-value-container, .count-value-container { display: flex; align-items: center; flex-wrap: nowrap; gap: 3px; flex: 1; justify-content: flex-start; margin-left: 4px; }
          .score-value, .count-value { font-weight: 600; color: #303133; }
          .pending-count { color: #F56C6C; }
          .score-separator, .count-separator { color: #c0c4cc; font-weight: 400; padding: 0 1px; }
        }
        .fix-rate-container { margin-top: 8px; padding: 0 2px;
          .fix-rate-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
          .fix-rate-label { font-size: 12px; color: #606266; font-weight: 500; }
          .fix-rate-value { font-size: 12px; font-weight: 600; }
          ::v-deep .el-progress-bar__outer { background-color: #f0f2f5; border-radius: 4px; }
          ::v-deep .el-progress-bar__inner { border-radius: 4px; transition: width 0.6s ease; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); }
        }
      }
    }
  }
}

.no-data { display: flex; justify-content: center; align-items: center; height: 300px;
  .no-data-content { text-align: center; padding: 30px;
    i { font-size: 50px; color: #dcdfe6; margin-bottom: 20px; }
    .no-data-title { font-size: 18px; color: #606266; font-weight: 500; margin-bottom: 10px; display: block; }
    .no-data-tips { font-size: 14px; color: #909399; }
  }
}

.load-more, .loading-more, .no-more { text-align: center; margin-top: 10px; padding: 10px 0; .el-button { font-size: 14px; } }
.loading-more { display: flex; justify-content: center; align-items: center; gap: 5px; color: #909399; i { font-size: 16px; } }
.no-more { text-align: center; padding: 15px 0 20px; span { color: #999999; font-size: 12px; display: inline-block; position: relative; font-weight: 400; letter-spacing: 0.5px; }
  .copyright-info { text-align: center; width: 100%; margin: 15px auto 0; display: block; padding: 0 15px 15px; box-sizing: border-box;
    .mobile-copyright { text-align: center; width: 100%; color: #909399; font-size: 12px; line-height: 1.5; }
  }
}

/* 详情弹窗与题目列表（复用 TagMobile 风格） */
.tag-detail { .detail-header { border-bottom: 1px solid #ebeef5; padding-bottom: 15px; margin-bottom: 15px; position: relative; }
  .tag-stats-card { margin-top: 12px; .tag-stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 8px; }
    .tag-stat-item { background-color: #f9fafc; border-radius: 6px; padding: 8px 5px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      .stat-label { font-size: 12px; color: #909399; margin-bottom: 4px; text-align: center; }
      .stat-value { font-size: 14px; font-weight: 600; color: #303133; text-align: center; &.difficulty-badge { color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; } }
    }
  }
  .detail-fixed-header { border-bottom: 1px solid #ebeef5; padding-bottom: 15px; margin-bottom: 15px; position: sticky; top: 0; background-color: #ffffff; z-index: 10; .tag-title { font-size: 18px; font-weight: 600; color: #303133; margin: 0 0 15px 0; padding-right: 35px; word-break: break-word; overflow: hidden; text-overflow: ellipsis; } }
  .detail-scrollable-content { overflow-y: auto; flex: 1; min-height: 200px; max-height: calc(80vh - 220px); scrollbar-width: none; -ms-overflow-style: none; padding-bottom: 20px; &::-webkit-scrollbar { display: none; }
    .question-no-more { text-align: center; padding: 15px 0 25px; width: 100%; margin-top: 5px; span { color: #999999; font-size: 12px; line-height: 1.2; font-weight: 400; position: relative; display: inline-block; padding: 0 10px; } }
    .question-list { .question-item { background-color: #f9fafc !important; border-radius: 8px; padding: 12px; margin-bottom: 10px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05); border: 1px solid #ebeef5; position: relative;
      .question-fix-stamp { position: absolute; top: 10px; right: 10px; z-index: 2; .stamp-content { width: 30px; height: 30px; border-radius: 50%; background-color: rgba(103, 194, 58, 0.6); color: white; display: flex; justify-content: center; align-items: center; font-size: 14px; font-weight: bold; border: 1.5px solid rgba(255, 255, 255, 0.6); box-shadow: 0 0 5px rgba(0, 0, 0, 0.15); text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2); transform: rotate(-15deg); &.ak-stamp { background-color: rgba(247, 186, 42, 0.6); } } }
      .question-header { display: flex; align-items: center; margin-bottom: 12px; .question-seq { color: #ffffff; font-size: 12px; padding: 2px 8px; border-radius: 4px; margin-right: 10px; flex-shrink: 0; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
        .question-name { font-size: 14px; font-weight: 500; color: #303133; flex: 1; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; .question-name-link { color: #409EFF; text-decoration: none; transition: color 0.2s ease; &:hover { color: #66b1ff; text-decoration: underline; } }
          .replay-icon-badge { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; background: rgba(64, 158, 255, 0.15); color: #409EFF; margin-left: 2px; box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.06); }
        }
      }
      .question-info { .game-name-row { display: flex; align-items: center; margin-bottom: 10px; font-size: 13px; color: #606266; i { color: #E6A23C; margin-right: 5px; font-size: 14px; } span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } }
        .score-row { display: flex; gap: 15px; flex-wrap: wrap; .score-cell { display: flex; align-items: center; font-size: 13px; .score-label { color: #606266; font-weight: normal; margin-right: 4px; } i { font-size: 16px; } .score-value { font-weight: 600; } } }
      }
    }
  }
}

/* 删除通用弹窗类，改为原始内联实现（保留必要子元素样式） */
.dialog-body { scrollbar-width: none; -ms-overflow-style: none; &::-webkit-scrollbar { display: none; } }
/* 详情内容区域仍沿用原样式 */
.dialog-container .custom-dialog.detail-dialog { left: 4% !important; width: 92% !important; max-width: 520px !important; transform: none !important; }

/* 筛选弹窗样式（与 TagMobile 一致） */
.filter-dialog-content { padding: 0; .filter-title { font-size: 16px; margin: 0 0 10px 0; padding-bottom: 8px; } }
.filter-form { .el-form-item { margin-bottom: 10px; .el-form-item__label { padding-right: 5px; line-height: 32px; } .el-form-item__content { line-height: 32px; } } }
.filter-footer { margin-top: 10px; padding-top: 8px; text-align: right; .el-button { padding: 7px 15px; & + .el-button { margin-left: 8px; } } }
.date-range-container { display: flex; align-items: center; gap: 2px; width: 100%; .date-picker-item { flex: 1; } .date-separator { flex-shrink: 0; color: #909399; padding: 0 1px; font-size: 12px; } }
}
</style>

<!-- 全局样式：日期选择器定位与优化（与 TagMobile 保持一致） -->
<style>
.el-picker-panel { left: 50% !important; transform: translateX(-50%) !important; margin-left: 0 !important; margin-right: 0 !important; z-index: 3000 !important; }
.el-picker-panel .popper__arrow { display: none !important; }
.el-picker-panel .el-date-picker__body { padding: 10px 8px !important; }
.el-picker-panel .el-date-table th, .el-picker-panel .el-date-table td { padding: 2px !important; width: 30px !important; height: 30px !important; }
.el-picker-panel .el-date-picker__header { margin: 6px 0 !important; padding: 0 8px !important; }
.el-picker-panel .el-date-picker__header-label { font-size: 14px !important; padding: 0 2px !important; }

/* 统计弹窗样式（参考 QuestionMobile） */
.custom-dialog.stats-dialog { left: 4%; width: 92%; max-width: 520px; max-height: none; }
.custom-dialog.stats-dialog .dialog-body.statistics-dialog-body { max-height: none; overflow-y: visible; padding: 12px 10px; }
.mobile-statistics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 2px 0; max-height: none; overflow-y: visible; }
.mobile-statistics-grid .stat-item { border-radius: 8px; padding: 8px 5px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); transition: all 0.3s ease; position: relative; overflow: hidden; min-height: 65px; height: auto; }
.mobile-statistics-grid .stat-item:active { transform: scale(0.98); }
/* 渐变配色与 QuestionMobile 保持一致（仅用到前两项） */
.mobile-statistics-grid .stat-item:nth-child(1) { background: linear-gradient(to top, rgba(226,206,186,0.3) 0%, rgba(226,206,186,1) 100%); }
.mobile-statistics-grid .stat-item:nth-child(1) .stat-label, 
.mobile-statistics-grid .stat-item:nth-child(1) .stat-label i { color: #8D7B64; }
.mobile-statistics-grid .stat-item:nth-child(1) .stat-value { color: #6D5B44; }
.mobile-statistics-grid .stat-item:nth-child(2) { background: linear-gradient(to top, rgba(209,178,164,0.3) 0%, rgba(209,178,164,1) 100%); }
.mobile-statistics-grid .stat-item:nth-child(2) .stat-label, 
.mobile-statistics-grid .stat-item:nth-child(2) .stat-label i { color: #956B5A; }
.mobile-statistics-grid .stat-item:nth-child(2) .stat-value { color: #754B3A; }
.mobile-statistics-grid .stat-item .stat-value { font-size: 16px; font-weight: 600; margin-bottom: 5px; text-align: center; line-height: 1.2; }
.mobile-statistics-grid .stat-item .stat-label { font-size: 11px; display: flex; align-items: center; justify-content: center; }
.mobile-statistics-grid .stat-item .stat-label i { margin-right: 3px; font-size: 13px; }
</style>

<!-- 全局样式：强制 WrongbookMobile 详情弹窗定位覆盖（提高选择器优先级） -->
<style>
.wrongbook-mobile-container .dialog-container .custom-dialog.detail-dialog { left: 4% !important; width: 92% !important; max-width: 520px !important; transform: none !important; }
.wrongbook-mobile-container .dialog-container .dialog-mask { background-color: rgba(0,0,0,0.7) !important; }
</style>

